import { IconName } from '@tallo/design-system';

export type Amenity =
  | 'isFurnished'
  | 'hasBarbecueArea'
  | 'hasBasement'
  | 'hasBasketBallCourt'
  | 'hasBusinessCenter'
  | 'hasCableSatellite'
  | 'hasChildCare'
  | 'hasClubDiscount'
  | 'hasConcierge'
  | 'hasControlledAccess'
  | 'hasCourtyard'
  | 'hasDeck'
  | 'hasDisabledAccess'
  | 'hasDock'
  | 'hasDoorman'
  | 'hasElevator'
  | 'hasFencedYard'
  | 'hasFitnessCenter'
  | 'hasGarden'
  | 'hasGatedEntry'
  | 'hasGreenHouse'
  | 'hasHotTubSpa'
  | 'hasHouseKeeping'
  | 'hasIntercom'
  | 'hasJettedBathTub'
  | 'hasLawn'
  | 'hasNightPatrol'
  | 'hasOnSiteMaintenance'
  | 'hasOnSiteManagement'
  | 'hasPackageReceiving'
  | 'hasPlayGround'
  | 'hasPong'
  | 'hasPorch'
  | 'hasRaquetBallCourt'
  | 'hasSauna'
  | 'hasSecuritySystem'
  | 'hasSkylight'
  | 'hasSportsCourt'
  | 'hasSprinklerSystem'
  | 'hasSunDeck'
  | 'hasTennisCourt'
  | 'hasTVLounge'
  | 'hasVolleyBallCourt'
  | 'hasWetBar'
  | 'hasDishwasher'
  | 'hasWasher'
  | 'hasDryer'
  | 'hasFreezer'
  | 'hasGarbageDisposal'
  | 'hasMicrowave'
  | 'hasRangeOven'
  | 'hasRefrigerator'
  | 'hasTrashCompactor'
  | 'hasCeilingFan'
  | 'hasDoublePaneWindows'
  | 'hasHandrails'
  | 'hasLargeClosets'
  | 'hasMotherInLawUnit'
  | 'hasPatio'
  | 'hasStorageSpace'
  | 'hasVaultedCeiling'
  | 'hasWindowCoverings'
  | 'hasBalcony'
  | 'hasFireplace'
  | 'hasPool'
  | 'hasWasherDryer'
  | 'hasAirConditioning'
  | 'hasHeating';

type AmenitiyItems = {
  [key in Amenity]: boolean | null;
};

export interface AmenitiesDto extends AmenitiyItems {
  id: string;
}

export const amenityNamesMap: { [key in Amenity]: string } = {
  isFurnished: 'Furnished',
  hasBarbecueArea: 'Barbecue Area',
  hasBasement: 'Basement',
  hasBasketBallCourt: 'Basketball Court',
  hasBusinessCenter: 'Business Center',
  hasCableSatellite: 'Cable/Satellite',
  hasChildCare: 'Child Care',
  hasClubDiscount: 'Club Discount',
  hasConcierge: 'Concierge',
  hasControlledAccess: 'Controlled Access',
  hasCourtyard: 'Courtyard',
  hasDeck: 'Deck',
  hasDisabledAccess: 'Disabled Access',
  hasDock: 'Dock',
  hasDoorman: 'Doorman',
  hasElevator: 'Elevator',
  hasFencedYard: 'Fenced Yard',
  hasFitnessCenter: 'Fitness Center',
  hasGarden: 'Garden',
  hasGatedEntry: 'Gated Entry',
  hasGreenHouse: 'Green House',
  hasHotTubSpa: 'Hot Tub/Spa',
  hasHouseKeeping: 'House Keeping',
  hasIntercom: 'Intercom',
  hasJettedBathTub: 'Jetted Bath Tub',
  hasLawn: 'Lawn',
  hasNightPatrol: 'Night Patrol',
  hasOnSiteMaintenance: 'On Site Maintenance',
  hasOnSiteManagement: 'On Site Management',
  hasPackageReceiving: 'Package Receiving',
  hasPlayGround: 'Play Ground',
  hasPong: 'Pong',
  hasPorch: 'Porch',
  hasRaquetBallCourt: 'Raquet Ball Court',
  hasSauna: 'Sauna',
  hasSecuritySystem: 'Security System',
  hasSkylight: 'Skylight',
  hasSportsCourt: 'Sports Court',
  hasSprinklerSystem: 'Sprinkler System',
  hasSunDeck: 'Sun Deck',
  hasTennisCourt: 'Tennis Court',
  hasTVLounge: 'TV Lounge',
  hasVolleyBallCourt: 'Volley Ball Court',
  hasWetBar: 'Wet Bar',
  hasDishwasher: 'Dishwasher',
  hasWasher: 'Washer',
  hasDryer: 'Dryer',
  hasFreezer: 'Freezer',
  hasGarbageDisposal: 'Garbage Disposal',
  hasMicrowave: 'Microwave',
  hasRangeOven: 'Range/Oven',
  hasRefrigerator: 'Refrigerator',
  hasTrashCompactor: 'Trash Compactor',
  hasCeilingFan: 'Ceiling Fan',
  hasDoublePaneWindows: 'Double Pane Windows',
  hasHandrails: 'Handrails',
  hasLargeClosets: 'Large Closets',
  hasMotherInLawUnit: 'Mother In Law Unit',
  hasPatio: 'Patio',
  hasStorageSpace: 'Storage Space',
  hasVaultedCeiling: 'Vaulted Ceiling',
  hasWindowCoverings: 'Window Coverings',
  hasBalcony: 'Balcony',
  hasFireplace: 'Fireplace',
  hasPool: 'Pool',
  hasWasherDryer: 'Washer/Dryer',
  hasAirConditioning: 'Air Conditioning',
  hasHeating: 'Heating',
};

/** Several amenities share a picture where one stands for the kind, such as every court or every laundry machine */
export const amenityIconsMap: { [key in Amenity]: IconName } = {
  isFurnished: 'sofa',
  hasBarbecueArea: 'grill',
  hasBasement: 'stairs',
  hasBasketBallCourt: 'basketball',
  hasBusinessCenter: 'briefcase-01',
  hasCableSatellite: 'tv-01',
  hasChildCare: 'stroller',
  hasClubDiscount: 'tag-01',
  hasConcierge: 'concierge-bell',
  hasControlledAccess: 'lock-01',
  hasCourtyard: 'tree',
  hasDeck: 'balcony',
  hasDisabledAccess: 'wheelchair',
  hasDock: 'anchor',
  hasDoorman: 'user-01',
  hasElevator: 'elevator',
  hasFencedYard: 'fence',
  hasFitnessCenter: 'dumbbell',
  hasGarden: 'tree',
  hasGatedEntry: 'fence',
  hasGreenHouse: 'leaf',
  hasHotTubSpa: 'hot-tub',
  hasHouseKeeping: 'broom',
  hasIntercom: 'phone',
  hasJettedBathTub: 'bath',
  hasLawn: 'leaf',
  hasNightPatrol: 'shield-01',
  hasOnSiteMaintenance: 'wrench',
  hasOnSiteManagement: 'user-check-02',
  hasPackageReceiving: 'package',
  hasPlayGround: 'playground',
  hasPong: 'tennis-ball',
  hasPorch: 'balcony',
  hasRaquetBallCourt: 'tennis-ball',
  hasSauna: 'hot-tub',
  hasSecuritySystem: 'shield-01',
  hasSkylight: 'sun',
  hasSportsCourt: 'basketball',
  hasSprinklerSystem: 'droplet',
  hasSunDeck: 'sun',
  hasTennisCourt: 'tennis-ball',
  hasTVLounge: 'tv-01',
  hasVolleyBallCourt: 'basketball',
  hasWetBar: 'wine-glass',
  hasDishwasher: 'washing-machine',
  hasWasher: 'washing-machine',
  hasDryer: 'washing-machine',
  hasFreezer: 'fridge',
  hasGarbageDisposal: 'trash',
  hasMicrowave: 'microwave',
  hasRangeOven: 'oven',
  hasRefrigerator: 'fridge',
  hasTrashCompactor: 'trash',
  hasCeilingFan: 'fan',
  hasDoublePaneWindows: 'window',
  hasHandrails: 'stairs',
  hasLargeClosets: 'hanger',
  hasMotherInLawUnit: 'home-plus',
  hasPatio: 'sun',
  hasStorageSpace: 'package',
  hasVaultedCeiling: 'home-03',
  hasWindowCoverings: 'window',
  hasBalcony: 'balcony',
  hasFireplace: 'flame',
  hasPool: 'pool',
  hasWasherDryer: 'washing-machine',
  hasAirConditioning: 'snowflake-01',
  hasHeating: 'thermometer',
};
